import {
  isISOCountryCode,
  ISOCountryCode,
  localizedCountryName,
  parseCountryCode,
} from '@pmi.web/countries';
import { CSSProperties } from 'react';

export interface ICountryFlagProps {
  readonly countryCode: string;
  readonly withCountryName?: boolean;
  readonly language?: string;
  readonly style?: CSSProperties;
  readonly squared?: boolean;
}

const CDN_URL =
  'https://cdn.pm-international.com/assets-shared-frontend/country-flags';
const CDN_PATH_SQUARED = '1x1';
const CDN_PATH_HORIZONTAL = '4x3';
const CDN_SUFFIX = 'svg';

const INTERNATIONAL_COUNTRY_CODES = ['international', 'int'];

function isInternationalCountryCode(countryCode: string): boolean {
  return INTERNATIONAL_COUNTRY_CODES.includes(countryCode.toLowerCase());
}

export function CountryFlag({
  countryCode,
  withCountryName = false,
  language = 'en',
  style = {},
  squared = false,
}: ICountryFlagProps) {
  let countryName: string | undefined;
  let isoCountryCode:
    | ISOCountryCode
    | 'xx'
    | (typeof INTERNATIONAL_COUNTRY_CODES)[number] = 'XX';

  if (isInternationalCountryCode(countryCode)) {
    isoCountryCode = 'international';
  } else if (isISOCountryCode(countryCode)) {
    isoCountryCode = parseCountryCode(countryCode);
    countryName = localizedCountryName(isoCountryCode, language);
  } else {
    isoCountryCode = 'xx';
  }

  const flagUrl = `${CDN_URL}/${squared ? CDN_PATH_SQUARED : CDN_PATH_HORIZONTAL}/${isoCountryCode.toLowerCase()}.${CDN_SUFFIX}`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: 'inherit',
        height: 'inherit',
        alignItems: 'center',
        gap: '2px',
        flexShrink: 0,
      }}
    >
      <img
        alt={isoCountryCode}
        src={flagUrl}
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          width: '100%',
          maxWidth: 18,
          maxHeight: 18,
          aspectRatio: squared ? '1 / 1' : '4 / 3',
          boxShadow: '0 0 2px rgb(142 148 154)',
          borderRadius: 2,
          overflow: 'hidden',
          ...style,
        }}
      />
      {withCountryName && <span>{countryName}</span>}
    </div>
  );
}
